import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge from "@/components/app/StatusBadge";
import { AlertTriangle, CheckCircle2 } from "lucide-react";

export default function Discrepancies() {
  const [items, setItems] = useState([]);
  const [buckets, setBuckets] = useState([]);
  const [severity, setSeverity] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/discrepancies", { params: { severity: severity !== "all" ? severity : undefined } })
      .then(({ data }) => { setItems(data.items || []); setBuckets(data.by_kind || []); })
      .finally(() => setLoading(false));
  };
  useEffect(load, [severity]);

  const resolve = async (id, resolution) => {
    try { await api.post(`/discrepancies/${id}/resolve`, { resolution }); toast.success("Resolved"); load(); } catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4 max-w-[1600px]">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Asset Intelligence</div>
        <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight">Discrepancies</h1>
        <p className="text-sm text-slate-500 mt-0.5">Where systems disagree — each item is actionable.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
        {buckets.map((b) => (
          <div key={b.kind} className="bg-white border border-slate-200 rounded-md p-3">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 truncate">{b.kind.replace(/_/g, " ")}</div>
            <div className="text-2xl font-heading font-bold tabular-nums text-slate-900 mt-0.5">{b.count}</div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-3 flex gap-2">
        <Select value={severity} onValueChange={setSeverity}>
          <SelectTrigger className="h-9 w-40"><SelectValue placeholder="Severity" /></SelectTrigger>
          <SelectContent>
            {["all", "critical", "high", "medium", "low"].map((s) => <SelectItem key={s} value={s}><span className="capitalize">{s}</span></SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100">
        {loading ? (
          <div className="p-6 text-sm text-slate-500">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500"><CheckCircle2 className="h-6 w-6 text-emerald-600 mx-auto mb-2" /> No open discrepancies.</div>
        ) : items.map((d) => (
          <div key={d.id} className="flex items-center gap-3 px-4 py-3">
            <AlertTriangle className={`h-4 w-4 ${d.severity === "critical" ? "text-rose-600" : d.severity === "high" ? "text-amber-600" : "text-sky-600"}`} strokeWidth={1.7} />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-slate-900 font-medium">{d.label}</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-0.5">
                {d.kind.replace(/_/g, " ")} · {d.source_system} · {new Date(d.detected_at).toLocaleDateString()}
              </div>
            </div>
            <StatusBadge value={d.severity} />
            <div className="flex items-center gap-1.5">
              <Button size="sm" variant="outline" onClick={() => resolve(d.id, "confirm")}>Confirm</Button>
              <Button size="sm" variant="ghost" onClick={() => resolve(d.id, "dismiss")}>Dismiss</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
